import React, { useEffect, useRef, useState } from 'react';
import { SUPPORTED_LANGUAGES, type LanguageCode } from './config';
import { useTranslation } from './provider';

export interface LanguageSwitcherProps {
  /**
   * Called after the language changes. Apps use this to persist the choice
   * to `profiles.language` when a user is signed in. Keeping it a callback
   * keeps this package free of any Supabase dependency.
   */
  onLanguageChange?: (lang: LanguageCode) => void | Promise<void>;
  /** Compact variant shows the flag only (e.g. tight headers). */
  compact?: boolean;
  className?: string;
}

const ChevronDownIcon: React.FC<{ open: boolean }> = ({ open }) => (
  <svg
    width="14"
    height="14"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    className={`transition-transform ${open ? 'rotate-180' : ''}`}
    aria-hidden="true"
  >
    <path d="m6 9 6 6 6-6" />
  </svg>
);

export const LanguageSwitcher: React.FC<LanguageSwitcherProps> = ({
  onLanguageChange,
  compact = false,
  className,
}) => {
  const { lang, setLanguage, t } = useTranslation();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const close = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('click', close);
    return () => document.removeEventListener('click', close);
  }, []);

  const current = SUPPORTED_LANGUAGES.find((l) => l.code === lang) ?? SUPPORTED_LANGUAGES[0];

  const choose = (code: LanguageCode) => {
    setOpen(false);
    if (code === lang) return;
    setLanguage(code);
    void onLanguageChange?.(code);
  };

  return (
    <div className={`relative ${className ?? ''}`} ref={ref}>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="flex items-center gap-2 px-3 py-2 rounded-xl bg-slate-800/90 border border-slate-600/50 text-slate-200 hover:text-white hover:bg-slate-700/80 transition-all text-sm font-medium min-h-[40px]"
        aria-expanded={open}
        aria-haspopup="true"
        aria-label={t('common.language')}
      >
        <span className="text-base leading-none">{current.flag}</span>
        {!compact && <span>{current.label}</span>}
        <ChevronDownIcon open={open} />
      </button>
      {open && (
        <div className="absolute right-0 top-full mt-2 w-44 bg-slate-900 border border-slate-700 rounded-2xl shadow-2xl z-50 p-1.5">
          {SUPPORTED_LANGUAGES.map((option) => (
            <button
              key={option.code}
              type="button"
              onClick={() => choose(option.code)}
              className={`flex w-full items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium transition-all ${
                option.code === lang
                  ? 'bg-purple-600/20 text-purple-300 border border-purple-500/30'
                  : 'text-slate-300 hover:bg-slate-800 hover:text-white'
              }`}
            >
              <span className="text-base leading-none">{option.flag}</span>
              <span>{option.label}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
};
